import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, merge, skip, switchMap, take, takeUntil, tap, catchError, of } from 'rxjs';
import { ChallengesApiService } from '../../shared/api/challenges-api.service';
import { IdeasApiService } from '../../shared/api/ideas-api.service';
import { ChallengeDto } from '../../shared/api/challenges-api.types';
import { IdeaListItemDto } from '../../shared/api/ideas-api.types';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

type FeedTab = 'all' | 'trending' | 'newest' | 'under-review' | 'delivered';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, LoaderComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, OnDestroy {
  ideas: IdeaListItemDto[] = [];
  categories: { id: string; name: string }[] = [];
  latestChallenge: ChallengeDto | null = null;
  loading = false;
  errorMessage = '';
  page = 1;
  pageSize = 20;
  totalCount = 0;
  searchText = '';
  categoryFilter = '';
  statusFilter = '';
  scopeFilter = '';
  effortFilter = '';
  departmentFilter = '';
  feedTab: FeedTab = 'all';

  readonly statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'triage', label: 'Triage' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'need-more-info', label: 'Need More Info' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'planned', label: 'Planned' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'delivered', label: 'Delivered' }
  ];

  readonly scopeOptions = [
    { value: '', label: 'All Scopes' },
    { value: 'my-team', label: 'My Team' },
    { value: 'my-department', label: 'My Department' },
    { value: 'company-wide', label: 'Company-wide' },
    { value: 'customers', label: 'Customers' },
    { value: 'product', label: 'Product' }
  ];

  readonly effortOptions = [
    { value: '', label: 'All Effort' },
    { value: 'low', label: 'Low (days)' },
    { value: 'medium', label: 'Medium (weeks)' },
    { value: 'high', label: 'High (months)' }
  ];

  readonly departmentOptions = [
    { value: '', label: 'All Departments' },
    { value: 'HR', label: 'HR' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Delivery/PMO', label: 'Delivery/PMO' },
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Product', label: 'Product' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Customer Excellence', label: 'Customer Excellence' },
    { value: 'Operations', label: 'Operations' }
  ];

  readonly staticCategoryOptions = [
    { value: 'internal-improvement', label: 'Internal Improvement' },
    { value: 'new-product', label: 'New Product' },
    { value: 'product-enhancement', label: 'Product Enhancement' },
    { value: 'customer-request', label: 'Customer Request' },
    { value: 'cost-saving', label: 'Cost Saving' },
    { value: 'quality-risk', label: 'Quality & Risk' },
    { value: 'culture-people', label: 'Culture & People' }
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly page$ = new BehaviorSubject<number>(1);
  private readonly categoryFilter$ = new BehaviorSubject<string>('');
  private readonly search$ = new BehaviorSubject<string>('');
  private readonly statusFilter$ = new BehaviorSubject<string>('');
  private readonly scopeFilter$ = new BehaviorSubject<string>('');
  private readonly effortFilter$ = new BehaviorSubject<string>('');
  private readonly departmentFilter$ = new BehaviorSubject<string>('');
  private readonly feedTab$ = new BehaviorSubject<FeedTab>('all');
  private readonly categories$ = new BehaviorSubject<{ id: string; name: string }[]>([]);

  constructor(
    private readonly api: IdeasApiService,
    private readonly challengesApi: ChallengesApiService
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadLatestChallenge();
    this.bindIdeasQuery();
  }

  loadLatestChallenge(): void {
    this.challengesApi
      .getLatestChallenge()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (challenge) => {
          this.latestChallenge = challenge;
        }
      });
  }

  formatChallengeDeadline(deadlineUtc: string): string {
    const d = new Date(deadlineUtc);
    return d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' });
  }

  loadCategories(): void {
    this.api
      .getCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.categories = items ?? [];
          this.categories$.next(this.categories);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFeedTab(tab: FeedTab): void {
    const prev = this.feedTab;
    this.feedTab = tab;
    if (tab === 'under-review') {
      this.statusFilter = 'under-review';
    } else if (tab === 'delivered') {
      this.statusFilter = 'delivered';
    } else if (prev === 'under-review' || prev === 'delivered') {
      this.statusFilter = '';
    }
    this.page$.next(1);
    this.feedTab$.next(tab);
    this.statusFilter$.next(this.statusFilter);
  }

  private bindIdeasQuery(): void {
    const debouncedSearch$ = merge(
      this.search$.pipe(take(1)),
      this.search$.pipe(skip(1), debounceTime(300))
    ).pipe(distinctUntilChanged());

    combineLatest([
      this.page$,
      this.categoryFilter$,
      debouncedSearch$,
      this.statusFilter$,
      this.scopeFilter$,
      this.effortFilter$,
      this.departmentFilter$,
      this.feedTab$,
      this.categories$
    ])
      .pipe(
        tap(([page]) => {
          this.page = page;
          this.loading = true;
          this.errorMessage = '';
        }),
        switchMap(([page, categoryFilter, search, status, scope, effort, dept, tab, cats]) => {
          const categoryId = this.resolveCategoryId(categoryFilter, cats);
          const { status: apiStatus, sort } = this.apiFeedParams(tab, status);
          return this.api
            .getIdeasWithOptions({
              page,
              pageSize: this.pageSize,
              categoryId,
              search,
              status: apiStatus,
              scope,
              effort,
              department: dept,
              sort
            })
            .pipe(
              catchError(() => {
                this.errorMessage = 'Failed to load ideas. Please try again.';
                return of({ items: [], page, pageSize: this.pageSize, totalCount: 0 });
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.ideas = result.items ?? [];
        this.totalCount = result.totalCount ?? 0;
        this.loading = false;
      });
  }

  private apiFeedParams(tab: FeedTab, statusFromDropdown: string): { status?: string; sort?: string } {
    const sort = tab === 'trending' ? 'trending' : undefined;
    if (tab === 'under-review') {
      return { status: 'under-review', sort };
    }
    if (tab === 'delivered') {
      return { status: 'delivered', sort };
    }
    const st = (statusFromDropdown ?? '').trim();
    return { status: st || undefined, sort };
  }

  private resolveCategoryId(filterValue: string, cats: { id: string; name: string }[]): string {
    const v = (filterValue ?? '').trim();
    if (!v) {
      return '';
    }
    if (this.isGuid(v)) {
      return v;
    }
    const slug = (s: string) =>
      s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    const bySlug = cats.find((c) => slug(c.name) === v);
    if (bySlug) {
      return bySlug.id;
    }
    return '';
  }

  onSearchTextChange(value: string): void {
    this.searchText = value ?? '';
    this.page$.next(1);
    this.search$.next(this.searchText);
  }

  onCategoryFilterChange(value: string): void {
    this.categoryFilter = value ?? '';
    this.page$.next(1);
    this.categoryFilter$.next(this.categoryFilter);
  }

  onStatusFilterChange(value: string): void {
    this.statusFilter = value ?? '';
    if (this.feedTab === 'under-review' && this.statusFilter !== 'under-review') {
      this.feedTab = 'all';
      this.feedTab$.next('all');
    } else if (this.feedTab === 'delivered' && this.statusFilter !== 'delivered') {
      this.feedTab = 'all';
      this.feedTab$.next('all');
    }
    this.page$.next(1);
    this.statusFilter$.next(this.statusFilter);
  }

  onScopeFilterChange(value: string): void {
    this.scopeFilter = value ?? '';
    this.page$.next(1);
    this.scopeFilter$.next(this.scopeFilter);
  }

  onEffortFilterChange(value: string): void {
    this.effortFilter = value ?? '';
    this.page$.next(1);
    this.effortFilter$.next(this.effortFilter);
  }

  onDepartmentFilterChange(value: string): void {
    this.departmentFilter = value ?? '';
    this.page$.next(1);
    this.departmentFilter$.next(this.departmentFilter);
  }

  previousPage(): void {
    if (this.page <= 1) return;
    this.page$.next(this.page - 1);
  }

  nextPage(): void {
    if (this.page * this.pageSize >= this.totalCount) return;
    this.page$.next(this.page + 1);
  }

  getStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'Under Review',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'In Progress',
      8: 'Delivered',
      9: 'Archived',
      10: 'Need More Info'
    };
    return labels[status] ?? 'Unknown';
  }

  getUpvoteCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.upvoteCount ?? idea.voteCount ?? 0);
  }

  getDownvoteCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.downvoteCount ?? 0);
  }

  getCommentCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.commentCount ?? 0);
  }

  getStatusClass(status: number): string {
    const classes: Record<number, string> = {
      0: 'tag-draft',
      1: 'tag-submitted',
      2: 'tag-submitted',
      3: 'tag-approved',
      4: 'tag-approved',
      5: 'tag-rejected',
      6: 'tag-approved',
      7: 'tag-approved',
      8: 'tag-approved',
      9: 'tag-default',
      10: 'tag-submitted'
    };
    return classes[status] ?? 'tag-default';
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  private isGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value ?? '');
  }
}
